import {Injectable} from '@angular/core';
import ExcelJS from 'exceljs';
import {
  createDefaultFacet,
  createDefaultValueField,
  IDSFacet,
  IDSInfo,
  IDSRule,
  IDSSpecification,
  IDSValueField,
  isValueFieldEmpty,
  NecessityType,
  PROPERTY_DATA_TYPES,
  RELATIONS
} from '../models/ids.model';
import {IFC_ENTITY_GROUPS, groupMembers} from '../models/ifc-entity-groups';
import {dataTypeGroupOf} from '../models/ifc-data-type-groups';

export interface IdsImportResult {
  rule: IDSRule;
  /** Nicht-fatale Hinweise (z.B. übersprungene Sheets ohne passende Daten). */
  warnings: string[];
}

/** IFC-Versionen, die im Attributlisten-Import auswählbar sind (#2498: IFC 4.3 ergänzt). */
export const ATTRIBUTE_LIST_IFC_VERSIONS = ['IFC 2x3', 'IFC 4', 'IFC 4.3'] as const;
export type AttributeListIfcVersion = typeof ATTRIBUTE_LIST_IFC_VERSIONS[number];

export interface AttributeListImportOptions {
  trade?: string;
  ifcVersion?: AttributeListIfcVersion;
  lodLevel?: number;
}

/**
 * Zuordnung der auswählbaren Attributlisten-IFC-Version zu (a) der IDS-Schema-Version und (b) dem
 * Muster, an dem die zugehörige Entitäts-Spalte in Zeile 1 erkannt wird ("IFC 4.3 Entität + …").
 * Die Reihenfolge ist relevant: "IFC 4.3" enthält ebenfalls eine 4, muss also vor "IFC 4" greifen.
 * `fallbackColumn` ist die Spalte des bisherigen festen Layouts (B bzw. F), falls die Kopfzeile
 * fehlt; IFC 4.3 hat dort keine eigene Spalte und fällt auf die IFC-4-Spalte zurück.
 */
const ATTRIBUTE_LIST_IFC_VERSION_INFO: ReadonlyArray<{
  version: AttributeListIfcVersion;
  schemaVersion: string;
  headerPattern: RegExp;
  fallbackColumn: number;
}> = [
  {version: 'IFC 2x3', schemaVersion: 'IFC2X3', headerPattern: /2x3/, fallbackColumn: 2},
  {version: 'IFC 4.3', schemaVersion: 'IFC4X3', headerPattern: /4[x._]3/, fallbackColumn: 6},
  {version: 'IFC 4', schemaVersion: 'IFC4', headerPattern: /ifc4/, fallbackColumn: 6},
];

/** Über die Kopfzeile aufgelöste Spaltenbelegung einer Attributliste (#2315), siehe `resolveAttributeColumns`. */
interface AttributeListColumns {
  startRow: number;
  attribute: number;
  unit: number;
  /** `0`, wenn die Spalte in der Kopfzeile nicht gefunden wurde. */
  dataType: number;
  designation: number;
  pset: number;
  responsible: number;
  optionset: number;
  sectionScanEnd: number;
  loiColumns: Array<[number, number]>;
}

/**
 * Clientseitiger Ersatz für die BIC-BIM-Tool-Endpunkte `ids_excel_to_json` und
 * `convert-ids-to-json`: konvertiert die beiden IDS-Excel-Vorlagen direkt im Browser
 * in das UI-Modell (IDSRule), ohne einen externen API-Call.
 *
 * Spiegelt die Spalten-/Zeilenlogik von `idsImport.py` (IDS-Vorlage) und
 * `convert_ids_list_to_xml.py` (Attributliste) aus dem BIC-BIM-Tool-Repository.
 */
@Injectable({providedIn: 'root'})
export class IdsExcelImportService {

  // ═══════════ IDS-Vorlage (.xlsx) ═══════════

  async importIdsTemplate(file: File): Promise<IdsImportResult> {
    const workbook = await this.loadWorkbook(file);
    const warnings: string[] = [];

    const info = this.readMetadata(this.requireSheet(workbook, 'Metadaten'));

    const applicabilityBySpec = this.readFacetRows(this.requireSheet(workbook, 'Anwendbarkeit'), false);
    const requirementsBySpec = this.readFacetRows(this.requireSheet(workbook, 'Anforderungen'), true);

    const specSheet = this.requireSheet(workbook, 'Spezifikationen');
    const specifications: IDSSpecification[] = [];

    for (let row = 7; row <= specSheet.rowCount; row++) {
      const title = this.cellText(specSheet, row, 2);
      if (!title) continue;

      const ifcVersionRaw = this.cellText(specSheet, row, 3);
      const applicabilityText = this.cellText(specSheet, row, 4);
      const description = this.cellText(specSheet, row, 5) ?? undefined;
      const instructionRaw = this.cellText(specSheet, row, 6);
      const identifier = this.cellText(specSheet, row, 7) ?? undefined;

      specifications.push({
        id: crypto.randomUUID(),
        name: title,
        necessity: this.necessityFromGermanText(applicabilityText),
        instruction: (instructionRaw && instructionRaw.toLowerCase() !== 'keine') ? instructionRaw : undefined,
        ifcVersions: (ifcVersionRaw ?? '').split(/\s+/).filter(Boolean),
        identifier,
        description,
        applicability: this.mergeEntityFacets(applicabilityBySpec.get(title) ?? []),
        requirements: this.mergeEntityFacets(requirementsBySpec.get(title) ?? []),
      });
    }

    if (specifications.length === 0) {
      warnings.push('Es wurden keine Spezifikationen in der IDS-Vorlage gefunden.');
    }

    return {rule: {info, specifications}, warnings};
  }

  private readMetadata(sheet: ExcelJS.Worksheet): IDSInfo {
    return {
      title: this.cellText(sheet, 6, 3) ?? '',
      author: this.cellText(sheet, 7, 3) ?? undefined,
      description: this.cellText(sheet, 8, 3) ?? undefined,
      version: this.cellText(sheet, 9, 3) ?? undefined,
      date: this.formatDateCell(sheet.getRow(10).getCell(3).value),
      purpose: this.cellText(sheet, 11, 3) ?? undefined,
      milestone: this.cellText(sheet, 12, 3) ?? undefined,
      copyright: this.cellText(sheet, 13, 3) ?? undefined,
    };
  }

  /** Liest die Facetten-Zeilen eines Sheets (Anwendbarkeit ODER Anforderungen), gruppiert nach Spezifikationsname. */
  private readFacetRows(sheet: ExcelJS.Worksheet, isRequirement: boolean): Map<string, IDSFacet[]> {
    const bySpec = new Map<string, IDSFacet[]>();

    for (let row = 9; row <= sheet.rowCount; row++) {
      const specName = this.cellText(sheet, row, 2);
      if (!specName) continue;

      let cardinality: NecessityType = 'required';
      let metaDescription: string | undefined;
      let metaInstruction: string | undefined;
      let metaUri: string | undefined;
      if (isRequirement) {
        cardinality = this.necessityFromGermanText(this.cellText(sheet, row, 17));
        metaDescription = this.cellText(sheet, row, 18) ?? undefined;
        metaInstruction = this.cellText(sheet, row, 19) ?? undefined;
        metaUri = this.cellText(sheet, row, 20) ?? undefined;
      }

      const facets = this.extractFacetsFromRow(sheet, row, isRequirement, cardinality, {
        description: metaDescription, instruction: metaInstruction, uri: metaUri,
      });
      if (!facets.length) continue;

      const list = bySpec.get(specName) ?? [];
      list.push(...facets);
      bySpec.set(specName, list);
    }

    return bySpec;
  }

  /** Liest alle Facetten-Typen aus einer Zeile (Entität, Attribut, Eigenschaft, Klassifikation, Material, Teil von). */
  private extractFacetsFromRow(
    sheet: ExcelJS.Worksheet, row: number, isRequirement: boolean, cardinality: NecessityType,
    meta: {description?: string; instruction?: string; uri?: string},
  ): IDSFacet[] {
    const facets: IDSFacet[] = [];
    const applyMeta = (f: IDSFacet) => {
      if (!isRequirement) return;
      f.metaNecessity = cardinality;
      if (meta.description) f.metaDescription = meta.description;
      if (meta.instruction) f.metaInstruction = meta.instruction;
      if (meta.uri) f.metaUri = meta.uri;
    };

    const entityName = this.cellText(sheet, row, 3);
    if (entityName !== null) {
      const f = createDefaultFacet('entity');
      f.entityName = this.parseCellValue(entityName);
      const pred = this.cellText(sheet, row, 4);
      if (pred !== null) f.predefinedType = this.parseCellValue(pred);
      applyMeta(f);
      facets.push(f);
    }

    const attrName = this.cellText(sheet, row, 5);
    if (attrName !== null) {
      const f = createDefaultFacet('attribute');
      f.attributeName = this.parseCellValue(attrName);
      const attrValue = this.cellText(sheet, row, 6);
      if (attrValue !== null) f.attributeValue = this.parseCellValue(attrValue);
      applyMeta(f);
      facets.push(f);
    }

    const propName = this.cellText(sheet, row, 8);
    if (propName !== null) {
      const f = createDefaultFacet('property');
      const psetRaw = this.cellText(sheet, row, 7);
      if (psetRaw !== null) f.propertySet = this.parseCellValue(psetRaw);
      f.propertyName = this.parseCellValue(propName);
      const dtype = this.cellText(sheet, row, 9);
      if (dtype) f.propertyDataType = this.matchPropertyDataType(dtype);
      const propValue = this.cellText(sheet, row, 10);
      if (propValue !== null) f.propertyValue = this.parseCellValue(propValue);
      applyMeta(f);
      facets.push(f);
    }

    const classSystem = this.cellText(sheet, row, 11);
    const classValue = this.cellText(sheet, row, 12);
    if (classSystem !== null || classValue !== null) {
      const f = createDefaultFacet('classification');
      if (classSystem !== null) f.classificationSystem = this.parseCellValue(classSystem);
      if (classValue !== null) f.classificationValue = this.parseCellValue(classValue);
      applyMeta(f);
      facets.push(f);
    }

    const material = this.cellText(sheet, row, 13);
    if (material !== null) {
      const f = createDefaultFacet('material');
      f.materialName = this.parseCellValue(material);
      applyMeta(f);
      facets.push(f);
    }

    const partOfRel = this.cellText(sheet, row, 14);
    const partOfEntity = this.cellText(sheet, row, 15);
    if (partOfRel !== null || partOfEntity !== null) {
      const f = createDefaultFacet('partOf');
      if (partOfRel !== null) f.relation = this.matchRelation(partOfRel);
      if (partOfEntity !== null) f.partOfEntityName = this.parseCellValue(partOfEntity);
      const partOfPred = this.cellText(sheet, row, 16);
      if (partOfPred !== null) f.partOfPredefinedType = this.parseCellValue(partOfPred);
      applyMeta(f);
      facets.push(f);
    }

    return facets;
  }

  /**
   * IDS erlaubt nur eine Entity-Facette pro Anwendbarkeit/Anforderungen. Wurden mehrere
   * Entity-Zeilen für dieselbe Spezifikation erfasst, werden sie zu einer Enumeration
   * zusammengeführt (Gegenstück zu `_merge_entity_facets` in idsImport.py).
   */
  private mergeEntityFacets(facets: IDSFacet[]): IDSFacet[] {
    const entities = facets.filter(f => f.type === 'entity');
    const others = facets.filter(f => f.type !== 'entity');
    if (entities.length <= 1) return facets;

    const allNames: string[] = [];
    let predefinedType: IDSValueField | undefined;
    for (const ent of entities) {
      if (ent.entityName.dataType === 'Aufzählung') {
        allNames.push(...(ent.entityName.enumValues ?? []));
      } else if (ent.entityName.simpleValue) {
        allNames.push(ent.entityName.simpleValue);
      }
      if (!predefinedType && !isValueFieldEmpty(ent.predefinedType)) predefinedType = ent.predefinedType;
    }

    const merged = createDefaultFacet('entity');
    merged.entityName = allNames.length > 1
      ? {...createDefaultValueField(), dataType: 'Aufzählung', enumValues: allNames}
      : {...createDefaultValueField(), dataType: 'Wert', simpleValue: allNames[0] ?? ''};
    if (predefinedType) merged.predefinedType = predefinedType;
    merged.metaNecessity = entities[0].metaNecessity;
    merged.metaDescription = entities[0].metaDescription;
    merged.metaInstruction = entities[0].metaInstruction;
    merged.metaUri = entities[0].metaUri;

    return [merged, ...others];
  }

  /**
   * Wandelt einen rohen Zellwert in ein IDSValueField um.
   * Unterstützt: einfacher Wert, Aufzählung (";"), Muster ("MUSTER="), Bereich ("MIN=…MAX=…").
   */
  private parseCellValue(raw: string): IDSValueField {
    let text = raw.trim();
    if (!text) return createDefaultValueField();

    if (/^MUSTER=/i.test(text)) {
      return {...createDefaultValueField(), dataType: 'Muster', pattern: text.slice('MUSTER='.length)};
    }

    const minMatch = /MIN\s*=\s*(\S+)/i.exec(text);
    const maxMatch = /MAX\s*=\s*(\S+)/i.exec(text);
    if (minMatch || maxMatch) {
      return {
        ...createDefaultValueField(),
        dataType: 'Grenzen',
        rangeMin: minMatch ? minMatch[1].replace(',', '.') : undefined,
        rangeMax: maxMatch ? maxMatch[1].replace(',', '.') : undefined,
      };
    }

    if (text.includes(';')) {
      const values = text.split(';').map(v => v.trim()).filter(Boolean);
      if (values.length > 1) {
        return {...createDefaultValueField(), dataType: 'Aufzählung', enumValues: values};
      }
      text = values[0] ?? text;
    }

    return {...createDefaultValueField(), dataType: 'Wert', simpleValue: text};
  }

  private necessityFromGermanText(text: string | null): NecessityType {
    const t = (text ?? '').trim().toLowerCase();
    if (t === 'optional') return 'optional';
    if (t === 'verboten' || t === 'prohibited') return 'prohibited';
    return 'required';
  }

  // ═══════════ Attributliste (.xlsx) ═══════════

  async importAttributeList(file: File, options: AttributeListImportOptions = {}): Promise<IdsImportResult> {
    const trade = options.trade?.trim() ?? '';
    const ifcVersionOption = options.ifcVersion ?? 'IFC 4';
    const lodLevel = options.lodLevel ?? 500;

    const workbook = await this.loadWorkbook(file);
    const warnings: string[] = [];
    const specifications: IDSSpecification[] = [];
    const skipSheets = new Set(['Deckblatt', 'Kürzel', 'Anleitung']);

    for (const sheet of workbook.worksheets) {
      if (skipSheets.has(sheet.name)) continue;

      const componentName = this.cellText(sheet, 1, 1);
      if (!componentName) continue;

      const entityColumn = this.resolveEntityColumn(sheet, ifcVersionOption);
      if (entityColumn.warning) warnings.push(`Sheet '${sheet.name}': ${entityColumn.warning}`);
      const ifcEntityRaw = this.cellText(sheet, 2, entityColumn.column);

      if (!ifcEntityRaw) {
        warnings.push(`Sheet '${sheet.name}': Kein IFC-Entity gefunden.`);
        continue;
      }

      const ifcVersions = [this.schemaVersionOf(ifcVersionOption)];
      const entityWarning = this.parseAttributeListEntityCell(ifcEntityRaw, ifcVersions).warning;
      if (entityWarning) warnings.push(`Sheet '${sheet.name}': ${entityWarning}`);

      // Jede Spezifikation braucht eine eigene Entity-Facette (eigene id), daher pro Spec neu parsen.
      const createSpec = (name: string, description: string, conditions: IDSFacet[] = []): IDSSpecification => ({
        id: crypto.randomUUID(),
        name,
        necessity: 'required',
        ifcVersions,
        description,
        applicability: [this.parseAttributeListEntityCell(ifcEntityRaw, ifcVersions).facet, ...conditions],
        requirements: [],
      });

      const generalSpec = createSpec(componentName, `Attributprüfung für ${componentName}`);
      const sheetSpecs: IDSSpecification[] = [generalSpec];
      const specsByHeader = new Map<string, IDSSpecification>();
      let currentSpec = generalSpec;

      const columns = this.resolveAttributeColumns(sheet);
      for (const missing of this.missingAttributeHeaders(columns)) {
        warnings.push(`Sheet '${sheet.name}': Spalte "${missing}" nicht gefunden - wird ignoriert.`);
      }
      if (columns.loiColumns.length === 0) {
        warnings.push(`Sheet '${sheet.name}': Keine LOI-Spalten gefunden (Zelle "LOI" mit den Stufen in der Zeile darunter).`);
      }

      for (let row = columns.startRow; row <= sheet.rowCount; row++) {
        const attrName = this.cellText(sheet, row, columns.attribute);
        if (!attrName) continue;

        // Zwischenüberschrift: Attribut-Spalte gefüllt, die übrigen Spalten (bis vor LOI) leer
        // oder (bei verbundenen Zellen) mit demselben Text. Mit ":" öffnet sie eine eigene
        // Spezifikation, ohne ":" gehören die folgenden Zeilen wieder zur allgemeinen Spezifikation.
        if (this.isSectionHeaderRow(sheet, row, attrName, columns.attribute + 1, columns.sectionScanEnd)) {
          const conditions = this.parseSectionHeader(attrName);
          if (conditions.length === 0) {
            currentSpec = generalSpec;
            continue;
          }
          let spec = specsByHeader.get(attrName);
          if (!spec) {
            spec = createSpec(`${componentName} – ${attrName}`, `Attributprüfung für ${componentName} (${attrName})`, conditions);
            specsByHeader.set(attrName, spec);
            sheetSpecs.push(spec);
          }
          currentSpec = spec;
          continue;
        }

        const dataType = this.cellTextAt(sheet, row, columns.dataType);
        const designation = this.cellTextAt(sheet, row, columns.designation);
        const pset = this.cellTextAt(sheet, row, columns.pset);
        const responsible = this.cellTextAt(sheet, row, columns.responsible);
        const optionset = this.cellTextAt(sheet, row, columns.optionset);

        if (!this.matchesTrade(responsible, trade)) continue;
        if (!this.hasLoiX(sheet, row, lodLevel, columns.loiColumns)) continue;

        // Leere Datentyp-Spalte bleibt leer: kein stiller IfcText-Default, sonst prüft die Regel
        // einen Typ, den die Attributliste nie vorgegeben hat.
        const propFacet = createDefaultFacet('property');
        propFacet.propertySet = {...createDefaultValueField(), dataType: 'Wert', simpleValue: pset ?? ''};
        propFacet.propertyName = {...createDefaultValueField(), dataType: 'Wert', simpleValue: designation ?? attrName};
        if (dataType) propFacet.propertyDataType = this.matchPropertyDataType(dataType);
        propFacet.propertyValue = this.parseOptionset(optionset, dataType);
        propFacet.metaNecessity = 'required';

        currentSpec.requirements.push(propFacet);
      }

      const filledSpecs = sheetSpecs.filter(s => s.requirements.length > 0);
      if (filledSpecs.length > 0) {
        specifications.push(...filledSpecs);
      } else {
        warnings.push(`Sheet '${sheet.name}': Keine passenden Attribute für Disziplin='${trade}', LOI=${lodLevel}.`);
      }
    }

    if (specifications.length === 0) {
      warnings.push('Es wurden keine passenden Attribute gefunden. Bitte Disziplin, IFC-Version und LOI-Level prüfen.');
    }

    const info: IDSInfo = {
      title: 'Attributliste Import',
      description: `Generiert aus LOI-Attributliste (Disziplin: ${trade || 'Alle'}, IFC: ${ifcVersionOption}, LOI: ${lodLevel})`,
    };

    return {rule: {info, specifications}, warnings};
  }

  /**
   * Spaltenbelegung der Attributtabelle eines Sheets (#2315: dynamischer statt spaltenindex-fixer
   * Import). Wird über die Kopfzeile ("Attribute"/"ME"/"Datentyp"/...) aufgelöst statt über feste
   * Spaltennummern, damit ein Projekt eigene Zusatzspalten ergänzen kann, ohne den Import zu
   * brechen. Nicht gefundene Spalten werden als `0` markiert (siehe {@link cellTextAt}) und lösen
   * eine Warnung aus (siehe {@link missingAttributeHeaders}).
   */
  private resolveAttributeColumns(sheet: ExcelJS.Worksheet): AttributeListColumns {
    const HEADER_KEYS: Record<string, keyof AttributeListColumns> = {
      attribute: 'attribute', me: 'unit', datentyp: 'dataType', bezeichnung: 'designation',
      pset: 'pset', verantwortlich: 'responsible', optionset: 'optionset',
    };

    for (let row = 4; row <= sheet.rowCount; row++) {
      const found = new Map<keyof AttributeListColumns, number>();

      for (let col = 1; col <= sheet.columnCount; col++) {
        const text = this.cellText(sheet, row, col)?.trim().toLowerCase();
        if (text && HEADER_KEYS[text]) found.set(HEADER_KEYS[text], col);
      }

      if (!found.has('attribute') || !found.has('unit')) continue;

      const {loiCol, loiColumns} = this.resolveLoiColumns(sheet, row);
      return {
        startRow: row + 2,
        attribute: found.get('attribute')!,
        unit: found.get('unit')!,
        dataType: found.get('dataType') ?? 0,
        designation: found.get('designation') ?? 0,
        pset: found.get('pset') ?? 0,
        responsible: found.get('responsible') ?? 0,
        optionset: found.get('optionset') ?? 0,
        sectionScanEnd: found.get('optionset') ?? (loiCol != null ? loiCol - 1 : sheet.columnCount),
        loiColumns,
      };
    }

    // Fallback auf das bisherige feste Layout, falls die Kopfzeile nicht wie erwartet aussieht.
    // Die LOI-Spalten werden auch dann über den "LOI"-Header gesucht statt fest angenommen.
    return {
      startRow: 6, attribute: 1, unit: 2, dataType: 3, designation: 4, pset: 5, responsible: 6, optionset: 7,
      sectionScanEnd: 7, loiColumns: this.resolveLoiColumns(sheet).loiColumns,
    };
  }

  /**
   * Findet die LOI-Spalten über die Zelle "LOI" (#2577) statt über feste Spaltennummern: die
   * Stufen (100/200/...) stehen in der Zeile darunter, beginnend in der Spalte von "LOI".
   *
   * Gesucht wird zuerst in der Kopfzeile der Attributtabelle, sonst im ganzen Sheet. Ist "LOI"
   * über mehrere Spalten verbunden, liefert ExcelJS für jede Zelle des Verbunds denselben Text -
   * es zählt die erste, und der Verbund begrenzt den LOI-Block. Ohne Verbund reicht der Block so
   * weit, wie die Stufen-Zeile lückenlos Zahlen enthält.
   */
  private resolveLoiColumns(sheet: ExcelJS.Worksheet, headerRow?: number): {loiCol: number | null; loiColumns: Array<[number, number]>} {
    const isLoi = (row: number, col: number): boolean => this.cellText(sheet, row, col)?.trim().toLowerCase() === 'loi';
    const findInRow = (row: number): number | null => {
      for (let col = 1; col <= sheet.columnCount; col++) {
        if (isLoi(row, col)) return col;
      }
      return null;
    };

    let row = headerRow ?? 0;
    let loiCol = headerRow != null ? findInRow(headerRow) : null;
    for (let r = 1; loiCol == null && r <= sheet.rowCount; r++) {
      loiCol = findInRow(r);
      row = r;
    }
    if (loiCol == null) return {loiCol: null, loiColumns: []};

    const header = sheet.getCell(row, loiCol);
    let lastCol = sheet.columnCount;
    if (header.isMerged) {
      lastCol = loiCol;
      while (lastCol < sheet.columnCount && sheet.getCell(row, lastCol + 1).master.address === header.master.address) lastCol++;
    }

    const loiColumns: Array<[number, number]> = [];
    for (let col = loiCol; col <= lastCol; col++) {
      const level = this.cellNumber(sheet, row + 1, col);
      if (level == null) {
        if (header.isMerged) continue;
        break;
      }
      loiColumns.push([level, col]);
    }
    return {loiCol, loiColumns};
  }

  /** IDS-Schema-Version zur ausgewählten Attributlisten-IFC-Version ("IFC 4.3" -> "IFC4X3"). */
  private schemaVersionOf(version: AttributeListIfcVersion): string {
    return ATTRIBUTE_LIST_IFC_VERSION_INFO.find(info => info.version === version)?.schemaVersion ?? 'IFC4';
  }

  /**
   * Spalte der Entitäts-Zelle (Zeile 2) für die gewählte IFC-Version. Wird über die Überschrift in
   * Zeile 1 aufgelöst ("IFC 4.3 Entität + PredefinedType") statt über feste Spaltennummern, damit
   * eine Attributliste die Versionsspalten in beliebiger Reihenfolge führen oder um IFC 4.3
   * ergänzen kann (#2498).
   *
   * Fehlt die Spalte der gewählten Version, greift das bisherige feste Layout (B = IFC 2x3,
   * F = IFC 4). Für IFC 4.3 ist das die IFC-4-Spalte: die Entitätsnamen sind bis auf wenige
   * Umbenennungen identisch, ein Abbruch wäre unverhältnismäßig - es wird aber gewarnt.
   */
  private resolveEntityColumn(sheet: ExcelJS.Worksheet, version: AttributeListIfcVersion): {column: number; warning?: string} {
    const byVersion = new Map<AttributeListIfcVersion, number>();

    for (let col = 1; col <= sheet.columnCount; col++) {
      const text = this.cellText(sheet, 1, col);
      if (!text) continue;
      const normalized = text.toLowerCase().replace(/\s+/g, '');
      if (!normalized.includes('ifc')) continue;
      const info = ATTRIBUTE_LIST_IFC_VERSION_INFO.find(entry => entry.headerPattern.test(normalized));
      // Erste Spalte gewinnt: die Überschrift ist über die Versionsspalten hinweg verbunden, alle
      // Zellen des Verbunds liefern denselben Text.
      if (info && !byVersion.has(info.version)) byVersion.set(info.version, col);
    }

    const found = byVersion.get(version);
    if (found) return {column: found};

    const fallback = ATTRIBUTE_LIST_IFC_VERSION_INFO.find(info => info.version === version)!;
    if (version === 'IFC 4.3') {
      return {
        column: byVersion.get('IFC 4') ?? fallback.fallbackColumn,
        warning: 'Keine IFC-4.3-Spalte gefunden - es wird die IFC-4-Entität verwendet.',
      };
    }
    return {column: fallback.fallbackColumn};
  }

  /** Erwartete, aber in der Kopfzeile nicht gefundene Spalten (für eine Warnung im Import-Ergebnis). */
  private missingAttributeHeaders(columns: AttributeListColumns): string[] {
    const labels: Array<[keyof AttributeListColumns, string]> = [
      ['dataType', 'Datentyp'], ['designation', 'Bezeichnung'], ['pset', 'Pset'],
      ['responsible', 'Verantwortlich'], ['optionset', 'Optionset'],
    ];
    return labels.filter(([key]) => !columns[key]).map(([, label]) => label);
  }

  /**
   * Parst die Entity-Zelle (Zeile 2) der Attributliste. Erlaubt mehrere, mit "," oder ";"
   * getrennte Entitäten und je Entität einen optionalen PredefinedType in der Schreibweise
   * "IfcFooting.PILE_CAP".
   *
   * IDS lässt nur eine Entity-Facette mit je einem `name`/`predefinedType` zu. Ist der
   * PredefinedType nur bei einem Teil der Entitäten angegeben, würde eine Einschränkung die
   * übrigen Entitäten fälschlich ausschließen – dann wird er weggelassen und gewarnt.
   *
   * Entspricht die gesamte Zelle (ohne weitere Tokens) dem Namen einer Entitätengruppe (#2313,
   * z.B. "Alle Elemente") wird direkt eine `entityGroup`-Facette mit deren Mitgliedern erzeugt –
   * so muss niemand alle ~170 IFC-Klassen von Hand eintragen.
   */
  private parseAttributeListEntityCell(raw: string, ifcVersions: readonly string[]): {facet: IDSFacet; warning?: string} {
    const trimmed = raw.trim();
    const group = IFC_ENTITY_GROUPS.find(g => g.label.toLowerCase() === trimmed.toLowerCase());
    if (group) {
      const facet = createDefaultFacet('entityGroup');
      facet.entityName = {...createDefaultValueField(), dataType: 'Aufzählung', enumValues: groupMembers(group, ifcVersions)};
      return {facet};
    }

    const names: string[] = [];
    const predefinedTypes: string[] = [];
    let missingPredefinedType = false;

    for (const token of raw.split(/[,;]/).map(t => t.trim()).filter(Boolean)) {
      const dot = token.indexOf('.');
      const name = (dot > 0 ? token.slice(0, dot) : token).trim().toUpperCase();
      const predefinedType = dot > 0 ? token.slice(dot + 1).trim().toUpperCase() : '';
      if (!name) continue;

      names.push(name);
      if (!predefinedType) missingPredefinedType = true;
      else if (!predefinedTypes.includes(predefinedType)) predefinedTypes.push(predefinedType);
    }

    const facet = createDefaultFacet('entity');
    facet.entityName = names.length > 1
      ? {...createDefaultValueField(), dataType: 'Aufzählung', enumValues: names}
      : {...createDefaultValueField(), dataType: 'Wert', simpleValue: names[0] ?? raw.trim().toUpperCase()};

    if (predefinedTypes.length > 0 && missingPredefinedType) {
      return {
        facet,
        warning: `Für '${raw.trim()}' ist der PredefinedType nur bei einem Teil der Entitäten angegeben – der PredefinedType wurde nicht übernommen.`,
      };
    }

    if (predefinedTypes.length > 0) {
      facet.predefinedType = predefinedTypes.length > 1
        ? {...createDefaultValueField(), dataType: 'Aufzählung', enumValues: predefinedTypes}
        : {...createDefaultValueField(), dataType: 'Wert', simpleValue: predefinedTypes[0]};
    }

    return {facet};
  }

  /**
   * Erkennt eine Zwischenüberschrift: die Attribut-Spalte ist gefüllt, die übrigen Spalten bis vor
   * dem LOI-Block sind leer oder enthalten – weil die Überschrift über die Tabellenbreite
   * verbunden ist – denselben Text.
   */
  private isSectionHeaderRow(sheet: ExcelJS.Worksheet, row: number, headerText: string, scanFrom: number, scanTo: number): boolean {
    for (let col = scanFrom; col <= scanTo; col++) {
      const text = this.cellText(sheet, row, col);
      if (text !== null && text !== headerText) return false;
    }
    return true;
  }

  /**
   * Wandelt eine Zwischenüberschrift in die zusätzlichen Property-Facetten der Anwendbarkeit um.
   *
   * Mehrere Bedingungen lassen sich mit "&" verknüpfen (#2498, z.B.
   * "Material: Stahlbeton & Tragend: True") - IDS wertet alle Facetten einer Anwendbarkeit als
   * UND aus, jede Bedingung wird also eine eigene Facette. "&" gilt nur dann als Verknüpfung,
   * wenn JEDER Teil selbst eine "<Eigenschaft>: <Wert>"-Bedingung ist; sonst gehört das Zeichen
   * zum Wert (z.B. "Nutzung: Wohnen & Gewerbe") und die Überschrift bleibt eine Bedingung.
   *
   * Überschriften ohne ":" liefern eine leere Liste.
   */
  private parseSectionHeader(headerText: string): IDSFacet[] {
    const parts = headerText.split('&').map(p => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      const conditions = parts.map(part => this.parseSectionCondition(part));
      if (conditions.every(condition => condition !== null)) return conditions as IDSFacet[];
    }

    const single = this.parseSectionCondition(headerText);
    return single ? [single] : [];
  }

  /**
   * Eine einzelne Bedingung "<Eigenschaft>: <Wert>" als Property-Facette. Die Eigenschaft darf ihr
   * Property-Set mitbringen ("Pset_WallCommon.LoadBearing: True"); ohne Angabe bleibt das Feld
   * leer, was der Adapter beim Export zum Wildcard `.*` macht (beliebiges Pset).
   */
  private parseSectionCondition(headerText: string): IDSFacet | null {
    const separator = headerText.indexOf(':');
    if (separator < 0) return null;

    const propertyPart = headerText.slice(0, separator).trim();
    const valuePart = headerText.slice(separator + 1).trim();
    if (!propertyPart || !valuePart) return null;

    const facet = createDefaultFacet('property');
    const dot = propertyPart.indexOf('.');
    if (dot > 0) {
      facet.propertySet = {...createDefaultValueField(), dataType: 'Wert', simpleValue: propertyPart.slice(0, dot).trim()};
      facet.propertyName = {...createDefaultValueField(), dataType: 'Wert', simpleValue: propertyPart.slice(dot + 1).trim()};
    } else {
      facet.propertySet = createDefaultValueField();
      facet.propertyName = {...createDefaultValueField(), dataType: 'Wert', simpleValue: propertyPart};
    }
    facet.propertyValue = this.parseCellValue(valuePart);
    return facet;
  }

  private matchesTrade(responsible: string | null, trade: string): boolean {
    if (!trade) return true;
    if (!responsible) return true;
    if (responsible.includes('Alle') || responsible.includes('alle')) return true;
    return responsible.includes(trade);
  }

  private hasLoiX(sheet: ExcelJS.Worksheet, row: number, lodLevel: number, loiColumns: ReadonlyArray<[number, number]>): boolean {
    for (const [loi, col] of loiColumns) {
      if (loi > lodLevel) continue;
      const val = this.cellText(sheet, row, col);
      if (val?.trim().toUpperCase() === 'X') return true;
    }
    return false;
  }

  /**
   * Parst den Optionset-Wert einer Zeile (Attributliste). Unterstützt Muster, Bereiche,
   * Aufzählungen (";" oder "/") und ignoriert Beispielwerte ("z.B. ..."). Boolean-Datentypen
   * bekommen keine Enumeration-Restriction (nur Existenzprüfung).
   *
   * Trennzeichen innerhalb von Anführungszeichen gehören zum Wert (#2498): `"220/713"` ist ein
   * einzelner Wert und keine Aufzählung aus "220" und "713". Die Anführungszeichen selbst sind
   * nur Schreibweise und landen nicht in der Regel.
   */
  private parseOptionset(raw: string | null, dataType: string | null): IDSValueField {
    if (!raw) return createDefaultValueField();
    let text = raw.trim();
    if (!text || text === '-') return createDefaultValueField();
    if (text.toLowerCase().startsWith('z.b.')) return createDefaultValueField();

    const isBoolean = this.isBooleanIfcType(dataType);

    if (/^MUSTER=/i.test(text)) {
      return {...createDefaultValueField(), dataType: 'Muster', pattern: text.slice('MUSTER='.length)};
    }

    const minMatch = /MIN\s*=\s*([^\s;]+)/i.exec(text);
    const maxMatch = /MAX\s*=\s*([^\s;]+)/i.exec(text);
    if (minMatch || maxMatch) {
      return {
        ...createDefaultValueField(),
        dataType: 'Grenzen',
        rangeMin: minMatch ? minMatch[1].replace(',', '.') : undefined,
        rangeMax: maxMatch ? maxMatch[1].replace(',', '.') : undefined,
      };
    }

    for (const separator of [';', '/']) {
      if (!text.includes(separator)) continue;
      // Die Anführungszeichen bleiben in `text` erhalten, damit ein danach geprüftes Trennzeichen
      // (";" vor "/") ebenfalls noch sieht, welche Stellen maskiert sind.
      const values = this.splitOutsideQuotes(text, separator);
      if (values.length > 1) {
        return isBoolean
          ? createDefaultValueField()
          : {...createDefaultValueField(), dataType: 'Aufzählung', enumValues: values.map(v => this.stripQuotes(v))};
      }
      if (separator === ';' && values.length) text = values[0];
    }

    return {...createDefaultValueField(), dataType: 'Wert', simpleValue: this.stripQuotes(text)};
  }

  /**
   * Zerlegt einen Wert am Trennzeichen, überspringt dabei aber Trennzeichen innerhalb von
   * Anführungszeichen (#2498). Neben dem geraden `"` zählen auch die typografischen Varianten,
   * die Excels Autokorrektur daraus macht. Die Anführungszeichen bleiben in den Teilstücken
   * stehen - {@link stripQuotes} entfernt sie erst am Ende.
   */
  private splitOutsideQuotes(text: string, separator: string): string[] {
    const parts: string[] = [];
    let current = '';
    let inQuotes = false;

    for (const char of text) {
      if (this.isQuoteChar(char)) inQuotes = !inQuotes;
      if (char === separator && !inQuotes) {
        parts.push(current);
        current = '';
        continue;
      }
      current += char;
    }
    parts.push(current);

    return parts.map(p => p.trim()).filter(Boolean);
  }

  private isQuoteChar(char: string): boolean {
    return char === '"' || char === '“' || char === '”' || char === '„';
  }

  /** Entfernt ein umschließendes Anführungszeichen-Paar - nur dieses, innen liegende bleiben. */
  private stripQuotes(value: string): string {
    const trimmed = value.trim();
    if (trimmed.length < 2) return trimmed;
    if (!this.isQuoteChar(trimmed[0]) || !this.isQuoteChar(trimmed[trimmed.length - 1])) return trimmed;
    return trimmed.slice(1, -1).trim();
  }

  private isBooleanIfcType(dataType: string | null): boolean {
    const dt = (dataType ?? '').trim().toUpperCase();
    return dt === 'IFCBOOLEAN' || dt === 'IFCLOGICAL';
  }

  // ═══════════ Gemeinsame Hilfsfunktionen ═══════════

  /**
   * Ordnet einen eingetippten Datentyp einem bekannten Wert zu. Erkennt zuerst die BIC-
   * Datentypengruppen (#2316: "Text"/"Zahl"/"Ganzzahl"/"Wahrheitswert", case-insensitiv) und
   * normalisiert sie auf ihr XML-Token (`dataTypeGroupOf` macht das selbst schon
   * case-insensitiv); erst danach der bisherige Abgleich gegen konkrete IFC-Typnamen.
   */
  private matchPropertyDataType(dtype: string): string {
    const group = dataTypeGroupOf(dtype);
    // Label ("Text"), nicht group.token ("TEXT"): der Editor speichert beim Auswählen einer Gruppe
    // ebenfalls das Label (siehe `selectDataTypeGroup` in ids-facet.component.ts) - sonst würde der
    // importierte Wert im Dropdown in Großbuchstaben angezeigt statt wie dort ausgewählt.
    if (group) return group.label;
    const match = PROPERTY_DATA_TYPES.find(p => p.toUpperCase() === dtype.toUpperCase());
    return match ?? dtype;
  }

  private matchRelation(value: string): string {
    return RELATIONS.find(r => r.toUpperCase() === value.toUpperCase()) ?? value;
  }

  private async loadWorkbook(file: File): Promise<ExcelJS.Workbook> {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    return workbook;
  }

  private requireSheet(workbook: ExcelJS.Workbook, name: string): ExcelJS.Worksheet {
    const sheet = workbook.getWorksheet(name);
    if (!sheet) throw new Error(`Das Tabellenblatt "${name}" wurde in der Excel-Datei nicht gefunden.`);
    return sheet;
  }

  private cellText(sheet: ExcelJS.Worksheet, row: number, col: number): string | null {
    return this.valueToText(sheet.getRow(row).getCell(col).value);
  }

  /** Wie {@link cellText}, aber sicher für eine nicht gefundene Spalte (`col === 0`, siehe {@link AttributeListColumns}). */
  private cellTextAt(sheet: ExcelJS.Worksheet, row: number, col: number): string | null {
    return col > 0 ? this.cellText(sheet, row, col) : null;
  }

  /**
   * Liest eine LOI-Stufe aus der Zeile unter dem "LOI"-Header: die Zahl selbst (auch als Text oder
   * Formelergebnis) oder mit "LOI"-Präfix ("LOI 100", "LoI100").
   */
  private cellNumber(sheet: ExcelJS.Worksheet, row: number, col: number): number | null {
    const value = sheet.getRow(row).getCell(col).value;
    if (typeof value === 'number') return value;
    // Leere Zelle nicht als 0 werten (Number(null) === 0), sonst wird sie zu einer LOI-Stufe "0".
    const match = this.valueToText(value)?.match(/^(?:LOI\s*)?(\d+)$/i);
    return match ? Number(match[1]) : null;
  }

  private valueToText(value: ExcelJS.CellValue): string | null {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) return null;
    if (typeof value === 'object') {
      if ('richText' in value) return value.richText.map(t => t.text).join('').trim() || null;
      if ('result' in value) return this.valueToText((value as {result: ExcelJS.CellValue}).result);
      if ('text' in value) return String((value as {text: unknown}).text).trim() || null;
      return null;
    }
    const text = String(value).trim();
    return text || null;
  }

  private formatDateCell(value: ExcelJS.CellValue): string | undefined {
    if (value === null || value === undefined) return undefined;
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return this.valueToText(value) ?? undefined;
  }
}
